import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ThemeToggle from "../../../components/ThemeToggle";
import ENDPOINTS from "../../../api/endpoint";
import { showErrorToast, showSuccessToast } from "../../../components/Toast";
import Loader from "../../../components/Loader";

export default function Mfa({ theme, setTheme }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  const pending = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("pending_login")) || {};
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    if (!pending?.email || !pending?.password) {
      showErrorToast("Session expired. Please login again");
      navigate("/");
    }
  }, [navigate, pending]);

  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return; // allow only single digit
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0)
      inputsRef.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5)
      inputsRef.current[index + 1]?.focus();
  };

  const otpCode = digits.join("");

  const handlePaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    const onlyDigits = pasted.replace(/\D/g, "").slice(0, 6);
    if (!onlyDigits) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < onlyDigits.length; i += 1) {
      next[i] = onlyDigits[i];
    }
    setDigits(next);
    const lastIndex = Math.min(onlyDigits.length, 6) - 1;
    if (lastIndex >= 0) {
      inputsRef.current[lastIndex]?.focus();
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      showErrorToast("Enter 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.VERIFY_MFA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pending.email, otp_code: otpCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.detail || "Invalid or expired OTP ❌");
        throw new Error(data.detail || "verify mfa failed");
      }

      // Save tokens and user info
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("role", data.role);
      localStorage.setItem("company_id", data.company_id);
      localStorage.setItem("user_id", data.user_id);
      localStorage.removeItem("pending_login");

      showSuccessToast("Verification successful ✅");
      navigate("/home");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!pending?.email || !pending?.password) return;
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.SEND_MFA_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pending.email,
          password: pending.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.detail || "Failed to resend OTP ❌");
        throw new Error(data.detail || "resend mfa failed");
      }
      showSuccessToast("OTP resent 📩");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative">
      {loading && <Loader />}

      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 lg:px-20 bg-white dark:bg-gray-900 transition-colors duration-300 relative">
        <div className="absolute top-4 right-4 md:hidden">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>

        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Enter OTP
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We sent a 6-digit code to your email
          </p>

          <form className="space-y-6" onSubmit={verifyOtp}>
            <div
              className="flex items-center justify-between gap-2"
              onPaste={handlePaste}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChange(i, e.target.value.slice(-1))}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-12 h-12 text-center text-xl border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-indigo-900 text-white font-medium rounded-md shadow hover:bg-indigo-800 transition disabled:opacity-50"
            >
              Verify
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={resendOtp}
              disabled={loading}
              className="text-indigo-900 dark:text-white hover:underline disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>
        </div>
      </div>

      <div className="hidden md:flex w-1/2 bg-indigo-900 text-white flex-col items-center justify-center relative">
        <h2 className="text-5xl font-bold">Secure Chat AI</h2>
      </div>
    </div>
  );
}
